<template>
  <div class="bg-grey-2 q-pa-sm q-mb-md" style="border-radius: 4px;">
    <div class="row items-center q-gutter-md text-body2">
      <!-- Buildings Badge -->
      <q-badge color="grey-7" outline>
        🏢 Buildings: {{ stats.buildings }}
      </q-badge>

      <!-- Levels Badge -->
      <q-badge color="grey-7" outline>
        📊 Levels: {{ stats.levels }}
      </q-badge>

      <!-- Spaces Badge -->
      <q-badge color="grey-7" outline>
        🚪 Spaces: {{ stats.spaces }}
      </q-badge>

      <!-- Equipment Badge -->
      <q-badge color="grey-7" outline>
        ⚙️ Equipment: {{ stats.equipment }}
      </q-badge>

      <!-- Devices Badge -->
      <q-badge color="grey-7" outline>
        📱 Devices: {{ stats.devices }}
      </q-badge>

      <!-- Points Badge -->
      <q-badge color="grey-7" outline>
        📍 Points: {{ stats.points }}
      </q-badge>
    </div>
  </div>
</template>

<script setup>
  import { computed } from 'vue';

  /**
   * BrickStatsPanel Component
   *
   * Displays hierarchical statistics for BRICK Schema hierarchy
   * including counts of buildings, levels, spaces, equipment, devices, and points.
   *
   * Props:
   * - hierarchyStats: Object with building, level, space, equipment, device, point counts
   *
   * Example:
   * <BrickStatsPanel :hierarchy-stats="hierarchyStats" />
   */

  const props = defineProps({
    /**
     * Hierarchy statistics object with entity counts
     * @type {Object}
     * @property {number} buildings - Count of buildings
     * @property {number} levels - Count of levels
     * @property {number} spaces - Count of spaces
     * @property {number} equipment - Count of equipment items
     * @property {number} devices - Count of devices
     * @property {number} points - Count of points
     */
    hierarchyStats: {
      type: Object,
      required: true,
      default: () => ({
        buildings: 0,
        levels: 0,
        spaces: 0,
        equipment: 0,
        devices: 0,
        points: 0,
      }),
    },
  });

  /**
   * Computed stats
   * Returns the hierarchy statistics from props with safe defaults
   */
  const stats = computed(() => ({
    buildings: props.hierarchyStats?.buildings || 0,
    levels: props.hierarchyStats?.levels || 0,
    spaces: props.hierarchyStats?.spaces || 0,
    equipment: props.hierarchyStats?.equipment || 0,
    devices: props.hierarchyStats?.devices || 0,
    points: props.hierarchyStats?.points || 0,
  }));
</script>

<style scoped>
/* Stats panel styling */
.bg-grey-2 {
  border-left: 4px solid #e0e0e0;
  transition: all 0.3s ease;
}

.bg-grey-2:hover {
  background-color: #f5f5f5;
  border-left-color: #bdbdbd;
}

/* Responsive badge gap adjustment */
@media (max-width: 768px) {
  .q-gutter-md {
    gap: 0.5rem !important;
  }

  .text-body2 {
    font-size: 0.85rem;
  }
}
</style>
